import React from 'react';
import { Plus, Trash2, FileSignature, TrendingUp, Download, Search, X, ChevronDown } from 'lucide-react';
import { projectService } from '../../services/projectService';
import { INSTALLATION_TRADES } from '../../../../verve-teams/utils/teamMemberLabels';
import { DataTable } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { useOverviewTables } from '../../hooks/useOverviewTables';
import { TEAM_COLUMNS, TASK_COLUMNS, TRANSACTION_COLUMNS } from '../../constants/overviewTableConstants';
import { MemberTransactionsModal } from './MemberTransactionsModal';

const memberId = (fo) => (typeof fo === 'object' ? fo._id : fo);
const memberIds = (project) => (project.fieldOfficers?.map((fo) => String(memberId(fo))) || []);

export const TeamAssignmentForm = ({ project, onUpdate, onClose }) => {
  const [officers, setOfficers] = React.useState([]);
  const [selectedMembers, setSelectedMembers] = React.useState([]);
  const [selectedRole, setSelectedRole] = React.useState('');
  const [stagedOfficerId, setStagedOfficerId] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [assignError, setAssignError] = React.useState('');
  
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await projectService.getFieldOfficers();
        const activeOnly = (data || []).filter((u) => u?.status !== 'Inactive');
        if (!cancelled) setOfficers(activeOnly);
      } catch (err) {
        console.error(err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (project?.fieldOfficers) {
       setSelectedMembers(project.fieldOfficers);
    }
  }, [project]);

  const pool = officers.filter(o => !selectedMembers.find(sm => (sm._id || sm) === o._id));
  
  // Dynamically get all unique roles from the available pool of officers
  const uniqueRoles = React.useMemo(() => {
    const rolesSet = new Set();
    officers.forEach(o => {
      const roleStr = o.installationTrade || o.role;
      if (roleStr && roleStr.toLowerCase() !== 'civil') rolesSet.add(roleStr.toLowerCase());
    });
    return Array.from(rolesSet).map(r => r.charAt(0).toUpperCase() + r.slice(1)).sort();
  }, [officers]);

  const availableOfficers = selectedRole 
      ? pool.filter(o => String(o.installationTrade || o.role).toLowerCase() === String(selectedRole).toLowerCase())
      : [];

  const handleAdd = () => {
     const off = pool.find(o => String(o._id) === String(stagedOfficerId));
     if (off) {
        setSelectedMembers(prev => [...prev, off]);
        setStagedOfficerId('');
     }
  };

  const handleRemove = (id) => {
     setSelectedMembers(prev => prev.filter(m => (m._id || m) !== id));
  };

  const handleSaveAll = async () => {
    setAssignError('');
    setIsSubmitting(true);
    try {
      const newIds = selectedMembers.map(m => m._id || m);
      await projectService.updateProject(project._id, { fieldOfficers: newIds });
      const updatedProject = await projectService.getProjectById(project._id);
      if (onUpdate) onUpdate(updatedProject);
      onClose();
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Could not assign team members';
      setAssignError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 font-poppins">
      {assignError && <p className="text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{assignError}</p>}
      
      {/* Member Selection Area */}
      <div className="flex flex-col sm:flex-row items-end gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
         <div className="w-full sm:w-[220px]">
            <label className="text-[12px] font-semibold text-slate-600 mb-1.5 block">Select Role</label>
            <div className="relative">
               <select
                  value={selectedRole}
                  onChange={(e) => {
                     setSelectedRole(e.target.value);
                     setStagedOfficerId('');
                  }}
                  className="w-full h-[40px] px-3 text-[13px] border border-slate-300 rounded-lg text-slate-700 bg-white focus:outline-none focus:ring-1 focus:border-[#f97316] focus:ring-[#f97316]/20 appearance-none cursor-pointer"
               >
                  <option value="">-- Select Role --</option>
                  {uniqueRoles.map(role => (
                     <option key={role.toLowerCase()} value={role.toLowerCase()}>{role}</option>
                  ))}
               </select>
               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-slate-400" />
               </div>
            </div>
         </div>

         <div className="flex-1 w-full min-w-0">
            <label className="text-[12px] font-semibold text-slate-600 mb-1.5 block">Select Member</label>
            <div className="relative">
               <select
                  value={stagedOfficerId}
                  onChange={(e) => setStagedOfficerId(e.target.value)}
                  disabled={!selectedRole || availableOfficers.length === 0}
                  className="w-full h-[40px] px-3 text-[13px] border border-slate-300 rounded-lg text-slate-700 bg-white focus:outline-none focus:ring-1 focus:border-[#f97316] focus:ring-[#f97316]/20 appearance-none cursor-pointer disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
               >
                  <option value="">
                     {!selectedRole ? 'Select a role first' : availableOfficers.length === 0 ? `No available ${selectedRole}s` : '-- Select Member --'}
                  </option>
                  {availableOfficers.map(m => (
                     <option key={m._id} value={m._id}>{m.name} {m.phone ? `- ${m.phone}` : ''}</option>
                  ))}
               </select>
               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-slate-400" />
               </div>
            </div>
         </div>

         <Button
            variant="orange"
            onClick={handleAdd}
            disabled={!stagedOfficerId}
            className="h-[40px] whitespace-nowrap rounded-lg px-6 font-bold w-full sm:w-auto"
         >
            <Plus className="w-4 h-4 mr-2" />
            Add
         </Button>
      </div>

      {/* Selected Members Table */}
      <div className="border border-slate-200 rounded-xl overflow-hidden mt-2 bg-white shadow-sm">
         <table className="w-full text-left border-collapse">
            <thead>
               <tr className="bg-slate-50/80 border-b border-slate-200">
                  <th className="px-5 py-3.5 text-[11px] font-black text-slate-500 uppercase tracking-widest w-1/2">Name</th>
                  <th className="px-5 py-3.5 text-[11px] font-black text-slate-500 uppercase tracking-widest w-1/3">Role Name</th>
                  <th className="px-5 py-3.5 text-[11px] font-black text-slate-500 uppercase tracking-widest w-1/6 text-right">Action</th>
               </tr>
            </thead>
            <tbody>
               {selectedMembers.length === 0 ? (
                  <tr>
                     <td colSpan="3" className="px-5 py-10 text-center text-slate-400 text-[13px] italic bg-white">
                        No team members assigned yet.
                     </td>
                  </tr>
               ) : (
                  selectedMembers.map((m, idx) => (
                     <tr key={m._id || idx} className="bg-white border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3.5">
                           <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 text-slate-600 flex items-center justify-center font-bold text-[12px]">
                                 {(m.name?.[0] || 'M').toUpperCase()}
                              </div>
                              <div className="flex flex-col">
                                 <span className="text-[13px] font-bold text-slate-800">{m.name}</span>
                                 {m.phone && <span className="text-[11px] text-slate-400 font-medium">{m.phone}</span>}
                              </div>
                           </div>
                        </td>
                        <td className="px-5 py-3.5">
                           <span className="text-[11px] uppercase font-black tracking-widest text-[#2b448f] bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-md">
                              {m.installationTrade || m.role || 'Member'}
                           </span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                           <button
                              onClick={() => handleRemove(m._id || m)}
                              className="text-slate-400 hover:text-rose-500 p-2 rounded-lg hover:bg-rose-50 transition-colors"
                              title="Remove Member"
                           >
                              <Trash2 className="w-4 h-4" />
                           </button>
                        </td>
                     </tr>
                  ))
               )}
            </tbody>
         </table>
      </div>

      {/* Footer Actions */}
      <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-5 mt-2">
        <Button
           variant="secondary"
           onClick={onClose}
           className="w-full sm:flex-1 h-11 rounded-lg text-[13px] font-bold"
        >
           Cancel
        </Button>
        <Button
           variant="orange"
           onClick={handleSaveAll}
           disabled={isSubmitting}
           loading={isSubmitting}
           className="w-full sm:flex-1 h-11 rounded-lg text-[13px] font-bold"
        >
           {isSubmitting ? '' : 'Save Assignments'}
        </Button>
      </div>
    </div>
  );
};

export const OverviewActivityTables = ({ 
  project, 
  formatCurrency, 
  onUpdate, 
  teamActionsLocked = false,
  hideTransactions = false,
  hideTasks = false,
  onAddMemberClick,
  onRequestExpense
}) => {
  const { tasks, transactionRows, formatRowDate } = useOverviewTables(project);
  
  const [viewMemberTxs, setViewMemberTxs] = React.useState(null);

  const getMemberTransactions = (member) => {
    const txs = project?.paymentSummary?.transactions || [];
    return txs.filter(t => {
      if (t.kind !== 'expense') return false;
      const uId = t.requestedBy?._id?.toString() || t.requestedBy?.id?.toString() || (typeof t.requestedBy === 'string' ? t.requestedBy : null) || t.createdBy?._id?.toString() || (typeof t.createdBy === 'string' ? t.createdBy : null);
      if (!uId || uId !== member._id?.toString()) return false;
      return t.requestStatus === 'paid' || t.requestStatus === 'approved' || t.requestStatus === 'partial_approved' || t.requestStatus === 'pending';
    });
  };

  const handleViewTransactions = (member) => {
    setViewMemberTxs(member);
  };

  const handleExportTransactions = (member) => {
    const txs = getMemberTransactions(member);
    if (!txs.length) {
      alert("This member has no approved expense transactions to export.");
      return;
    }
    
    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '""';
      if (typeof val === 'number') return val;
      const str = String(val).replace(/"/g, '""').replace(/\r?\n|\r/g, ' ');
      return `"${str}"`;
    };

    const headers = ['Transaction ID', 'Member Name', 'Client Name', 'Project ID', 'Customer Project ID', 'Site Name', 'Customer', 'Project Status', 'PO Value', 'Budget', 'Location', 'Date', 'Category', 'Reason', 'Status', 'Requested Amount', 'Approved Amount', 'Disbursed Amount', 'Payment Method', 'Ref ID', 'Bank Name', 'UPI/Phone', 'Receiver Name', 'Cheque Number'];
    const rows = txs.map(t => [
      escapeCsv(t.transactionId || t.txnId || (t._id ? `TXN-${String(t._id).slice(-6).toUpperCase()}` : '')),
      escapeCsv(member?.name || ''),
      escapeCsv(project?.customerName || ''),
      escapeCsv(project?.projectId || ''),
      escapeCsv(project?.applicationId || ''),
      escapeCsv(project?.siteName || ''),
      escapeCsv(project?.projectName || ''),
      escapeCsv(project?.status || ''),
      escapeCsv(project?.projectValue || 0),
      escapeCsv(project?.targetCost || 0),
      escapeCsv([project?.address?.city, project?.address?.state].filter(Boolean).join(', ') || ''),
      escapeCsv(t.transactionDate ? new Date(t.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''),
      escapeCsv(t.categoryLabel || t.expenseCategory || ''),
      escapeCsv(t.expenseReason || ''),
      escapeCsv(t.requestStatus || ''),
      escapeCsv(t.amount || 0),
      escapeCsv(t.approvedAmount || 0),
      escapeCsv(t.disbursedAmount || 0),
      escapeCsv(t.paymentMethod || ''),
      escapeCsv(t.refId || ''),
      escapeCsv(t.bankName || ''),
      escapeCsv(t.upiPhone || ''),
      escapeCsv(t.receiverName || ''),
      escapeCsv(t.chequeNumber || '')
    ].join(','));
    
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${member.name || 'Member'}_transactions.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportAllTransactions = () => {
    if (!project?.fieldOfficers?.length) {
      alert("No team members assigned to export.");
      return;
    }
    
    let allTxs = [];
    for (const member of project.fieldOfficers) {
      if (typeof member !== 'object') continue;
      const txs = getMemberTransactions(member);
      allTxs = allTxs.concat(txs.map(t => ({ ...t, _member: member })));
    }
    
    if (!allTxs.length) {
      alert("There are no approved expense transactions for any team member yet.");
      return;
    }

    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '""';
      if (typeof val === 'number') return val;
      const str = String(val).replace(/"/g, '""').replace(/\r?\n|\r/g, ' ');
      return `"${str}"`;
    };

    const headers = ['Transaction ID', 'Member Name', 'Client Name', 'Project ID', 'Customer Project ID', 'Site Name', 'Customer', 'Project Status', 'PO Value', 'Budget', 'Location', 'Date', 'Category', 'Reason', 'Status', 'Requested Amount', 'Approved Amount', 'Disbursed Amount', 'Payment Method', 'Ref ID', 'Bank Name', 'UPI/Phone', 'Receiver Name', 'Cheque Number'];
    const rows = allTxs.map(t => [
      escapeCsv(t.transactionId || t.txnId || (t._id ? `TXN-${String(t._id).slice(-6).toUpperCase()}` : '')),
      escapeCsv(t._member?.name || ''),
      escapeCsv(project?.customerName || ''),
      escapeCsv(project?.projectId || ''),
      escapeCsv(project?.applicationId || ''),
      escapeCsv(project?.siteName || ''),
      escapeCsv(project?.projectName || ''),
      escapeCsv(project?.status || ''),
      escapeCsv(project?.projectValue || 0),
      escapeCsv(project?.targetCost || 0),
      escapeCsv([project?.address?.city, project?.address?.state].filter(Boolean).join(', ') || ''),
      escapeCsv(t.transactionDate ? new Date(t.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''),
      escapeCsv(t.categoryLabel || t.expenseCategory || ''),
      escapeCsv(t.expenseReason || ''),
      escapeCsv(t.requestStatus || ''),
      escapeCsv(t.amount || 0),
      escapeCsv(t.approvedAmount || 0),
      escapeCsv(t.disbursedAmount || 0),
      escapeCsv(t.paymentMethod || ''),
      escapeCsv(t.refId || ''),
      escapeCsv(t.bankName || ''),
      escapeCsv(t.upiPhone || ''),
      escapeCsv(t.receiverName || ''),
      escapeCsv(t.chequeNumber || '')
    ].join(','));
    
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${project?.projectId || 'Project'}_all_team_transactions.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteMember = async (officer) => {
    if (teamActionsLocked) return;
    const currentIds = project.fieldOfficers.map((fo) => memberId(fo));
    const newIds = currentIds.filter((id) => String(id) !== String(officer._id));
    await projectService.updateProject(project._id, { fieldOfficers: newIds });
    if (onUpdate) onUpdate(await projectService.getProjectById(project._id));
  };

  const expensesMap = React.useMemo(() => {
    const map = {};
    const txs = project?.paymentSummary?.transactions || [];
    for (const t of txs) {
      if (t.kind === 'expense') {
        const uId = t.requestedBy?._id?.toString() || t.requestedBy?.id?.toString() || (typeof t.requestedBy === 'string' ? t.requestedBy : null) || t.createdBy?._id?.toString() || (typeof t.createdBy === 'string' ? t.createdBy : null);
        if (uId) {
          if (t.requestStatus === 'paid' || t.requestStatus === 'approved' || t.requestStatus === 'partial_approved') {
            const reqAmt = Number(t.amount) || 1;
            const booked = Number(t.approvedAmount ?? t.amount) || 0;
            const ratio = booked / reqAmt;
            const std = (Number(t.standardAmount) || 0) * ratio;
            const ep = (Number(t.epAmount) || 0) * ratio;
            const ns = (Number(t.nonStandardAmount) || 0) * ratio;
            const oth = (Number(t.othersAmount) || 0) * ratio;
            const tradeSum = std + ep + ns + oth;
            
            let applicable = booked;
            if (tradeSum > 0.01) {
              applicable = Math.max(0, booked - tradeSum);
            }
            map[uId] = (map[uId] || 0) + applicable;
          }
        }
      }
    }
    return map;
  }, [project?.paymentSummary?.transactions]);

  return (
    <div className="w-full flex flex-col gap-4">
      <div id="team-management" className="bg-white border border-slate-100 rounded-xl overflow-hidden flex flex-col scroll-mt-24">
        <div className="p-4 border-b border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleExportAllTransactions}
            className="flex items-center cursor-pointer gap-2 px-4 py-2 rounded-md text-[11px] font-semibold tracking-wider transition-all shadow-sm bg-transparent border border-emerald-600 text-emerald-600 hover:bg-emerald-50"
          >
            <Download className="w-3.5 h-3.5" />
            Export All
          </button>
          <button
            type="button"
            onClick={onAddMemberClick}
            disabled={teamActionsLocked}
            className={`flex items-center cursor-pointer gap-2 px-4 py-2 rounded-md text-[11px] font-semibold tracking-wider transition-all shadow-sm ${
              teamActionsLocked ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200' : 'bg-transparent border border-[#2b448f] text-[#2b448f] hover:bg-blue-50'
            }`}
          >
            <Plus className="w-3.5 h-3.5" />
            {project?.fieldOfficers?.length > 0 ? 'Update Member' : 'Add Member'}
          </button>
        </div>

        <DataTable 
          columns={TEAM_COLUMNS(handleDeleteMember, teamActionsLocked, expensesMap, formatCurrency, handleViewTransactions, handleExportTransactions, onRequestExpense, project?.projectId)}
          data={project?.fieldOfficers || []}
          noScroll={true}
        />
      </div>

      {!hideTasks && project?.advancePaymentStatus === 'Approved' && (
        <div className="bg-white border border-slate-100 rounded-xl overflow-hidden flex flex-col mt-4">
          <div className="p-6 border-b border-slate-100 flex items-center gap-3">
            <FileSignature className="w-5 h-5 text-[#2b448f]" />
            <h3 className="text-[13px] font-black text-slate-800 tracking-tight">Task assignments</h3>
          </div>
          <DataTable 
            columns={TASK_COLUMNS}
            data={tasks}
            noScroll={true}
          />
        </div>
      )}

      {!hideTransactions && (
        <div className="bg-white border border-slate-100 rounded-xl overflow-hidden flex flex-col mt-4">
          <div className="p-6 border-b border-slate-100 flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            <h3 className="text-[13px] font-black text-slate-800 tracking-tight">Transaction history</h3>
          </div>
          <DataTable 
            columns={TRANSACTION_COLUMNS(formatCurrency, formatRowDate)}
            data={transactionRows}
            noScroll={true}
          />
        </div>
      )}

      {viewMemberTxs && (
        <MemberTransactionsModal
          member={viewMemberTxs}
          project={project}
          transactions={getMemberTransactions(viewMemberTxs)}
          onClose={() => setViewMemberTxs(null)}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
};

